import React, { AnchorHTMLAttributes, forwardRef, ReactNode } from 'react';
import classNames from 'classnames';
import { Icon } from '../icon/icon';

interface ATagProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    /**
     * Label inside the link
     */
    label?: ReactNode;
    href: string;
    target?: '_blank' | '_self';
    /**
     * an icon name of an icon to add, note that only non-UI-icons are supported here,
     * ignored for links that are not of level 'simple'
     */
    icon?: string;
    /**
     * 'left' or 'right', controls position, defaults to 'left'
     */
    iconPosition?: 'left' | 'right';
}

export interface LinkProps extends ATagProps {
    /**
     * Type of Link (simple, inline or primary), defaults to 'simple'
     */
    level?: 'simple' | 'primary' | 'inline';
    /**
     * Whether or not the Link is disabled, defaults to false
     */
    disabled?: boolean;
}

const ATag = forwardRef<HTMLAnchorElement, ATagProps>((props, ref) => {
    const { label, icon, iconPosition = 'left', ...rest } = props;
    return (
        <a ref={ref} {...rest}>
            {icon && iconPosition === 'left' && <Icon iconName={icon} />}
            {label}
            {icon && iconPosition === 'right' && <Icon iconName={icon} />}
        </a>
    );
});
ATag.displayName = 'ATag';

/**
 * Representation of links
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
    const {
        level = 'simple',
        disabled = false,
        target = '_self',
        icon = null,
        iconPosition = 'left',
        className,
        style,
        ...rest
    } = props;

    if (level === 'inline') {
        return (
            <ATag
                ref={ref}
                target={target}
                className={className}
                style={style}
                {...rest}
            />
        );
    }

    const classes = classNames(
        'a-link',
        {
            'a-link--primary': level === 'primary',
            'a-link--simple': level === 'simple',
            '-disabled': disabled,
            '-icon-left': level === 'simple' && icon && iconPosition === 'left',
            '-icon-right':
                level === 'simple' && icon && iconPosition === 'right',
        },
        className,
    );

    return (
        <div className={classes} style={style}>
            <ATag
                ref={ref}
                target={target}
                icon={level === 'simple' ? icon : null}
                iconPosition={iconPosition}
                {...rest}
            />
        </div>
    );
});
Link.displayName = 'Link';
